import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, MoreVertical, Pencil, Plus, Power, Trash2 } from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { usePageActions } from "@/shared/hooks/usePageActions";
import { useToast } from "@/shared/providers/ToastProvider";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { StatusBadge } from "../components/StatusBadge";
import { FormField, inputClass, selectClass } from "../components/FormField";
import { statusOptions } from "../data";
import { useAdminBranches } from "../hooks/useAdminBranches";

type BranchActionsMenuProps = {
  isOpen: boolean;
  isActive: boolean;
  labels: {
    edit: string;
    deactivate: string;
    delete: string;
  };
  onToggle: () => void;
  onClose: () => void;
  onEdit: () => void;
  onDeactivate: () => void;
  onDelete: () => void;
};

function BranchActionsMenu({
  isOpen,
  isActive,
  labels,
  onToggle,
  onClose,
  onEdit,
  onDeactivate,
  onDelete,
}: BranchActionsMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  return (
    <div ref={menuRef} className="relative">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border text-muted-foreground transition-colors hover:bg-card-muted"
      >
        <MoreVertical size={14} />
      </button>

      {isOpen && (
        <div className="card-shadow absolute right-0 top-full z-50 mt-1 min-w-[160px] overflow-hidden rounded-xl border border-border bg-card p-1.5">
          <button
            type="button"
            onClick={onEdit}
            className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-foreground transition-colors hover:bg-card-muted"
          >
            <Pencil size={14} className="text-primary" />
            {labels.edit}
          </button>
          <button
            type="button"
            onClick={onDeactivate}
            disabled={!isActive}
            className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-foreground transition-colors hover:bg-card-muted disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Power size={14} className="text-yellow" />
            {labels.deactivate}
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-pink transition-colors hover:bg-pink-soft"
          >
            <Trash2 size={14} />
            {labels.delete}
          </button>
        </div>
      )}
    </div>
  );
}

export function BranchesPage() {
  const { t } = useTranslation();
  const labels = t.safecheck.branchesPage;
  const { notifyDeleted, navigate } = usePageActions();
  const { showToast } = useToast();
  const { branches, loading, error, usingApi, deactivate, remove } = useAdminBranches();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  const filtered = useMemo(() => {
    return branches.filter((branch) => {
      const bySearch = search
        ? `${branch.name} ${branch.province}`.toLowerCase().includes(search.toLowerCase())
        : true;
      const byStatus = statusFilter ? branch.status === statusFilter : true;
      return bySearch && byStatus;
    });
  }, [branches, search, statusFilter]);

  const closeMenu = () => setOpenMenuId(null);

  return (
    <>
      <Breadcrumbs
        items={[
          { label: t.safecheck.nav.employees, href: "/dashboard/employees" },
          { label: labels.pageTitle },
        ]}
      />

      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-foreground">{labels.pageTitle}</h1>
          {usingApi && (
            <p className="mt-1 text-xs text-muted">
              {loading ? "Chargement…" : `${branches.length} antenne(s) API`}
            </p>
          )}
          {error && <p className="mt-1 text-xs text-pink">{error}</p>}
        </div>
        <Link
          to="/dashboard/branches/create"
          className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary/90"
        >
          <Plus size={16} />
          {labels.newBranch}
        </Link>
      </div>

      <div className="theme-card p-5">
        <div className="mb-4 flex items-center gap-2">
          <h2 className="text-base font-semibold text-foreground">{labels.listTitle}</h2>
          <span className="flex h-6 min-w-6 items-center justify-center rounded-md bg-purple-soft px-2 text-xs font-semibold text-purple">
            {filtered.length}
          </span>
        </div>

        <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2">
          <FormField label={labels.search}>
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={labels.searchPlaceholder}
              className={inputClass}
            />
          </FormField>
          <FormField label={t.safecheck.fields.status}>
            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              className={selectClass}
            >
              <option value="">{labels.allStatuses}</option>
              {statusOptions
                .filter((option) => option.value)
                .map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
            </select>
          </FormField>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[800px]">
            <thead>
              <tr className="border-b border-border bg-card-muted/40">
                {[
                  labels.columns.name,
                  labels.columns.province,
                  labels.columns.phone,
                  labels.columns.email,
                  labels.columns.status,
                  labels.columns.actions,
                ].map((header) => (
                  <th
                    key={header}
                    className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-muted"
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((branch) => (
                <tr key={branch.id} className="border-b border-border last:border-0 hover:bg-card-muted/30">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                      <Building2 size={14} className="text-muted" />
                      {branch.name}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground">{branch.province}</td>
                  <td className="px-4 py-3 text-sm text-muted">{branch.phone || "—"}</td>
                  <td className="px-4 py-3 text-sm text-muted">{branch.email || "—"}</td>
                  <td className="px-4 py-3">
                    <StatusBadge variant={branch.status === "active" ? "success" : "neutral"}>
                      {branch.status === "active" ? t.safecheck.status.active : t.safecheck.status.inactive}
                    </StatusBadge>
                  </td>
                  <td className="px-4 py-3">
                    <BranchActionsMenu
                      isOpen={openMenuId === branch.id}
                      isActive={branch.status === "active"}
                      labels={labels.actions}
                      onToggle={() => setOpenMenuId((current) => (current === branch.id ? null : branch.id))}
                      onClose={closeMenu}
                      onEdit={() => {
                        closeMenu();
                        navigate(`/dashboard/branches/${branch.id}/edit`);
                      }}
                      onDeactivate={() => {
                        void (async () => {
                          try {
                            await deactivate(branch);
                            closeMenu();
                          } catch (err) {
                            showToast(err instanceof Error ? err.message : "Action impossible.");
                          }
                        })();
                      }}
                      onDelete={() => {
                        void (async () => {
                          try {
                            await remove(branch);
                            closeMenu();
                            notifyDeleted();
                          } catch (err) {
                            showToast(err instanceof Error ? err.message : "Suppression impossible.");
                          }
                        })();
                      }}
                    />
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-sm text-muted">
                    {loading ? "Chargement…" : "Aucune antenne."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
